/**
 * Visible breadcrumb trail + matching `BreadcrumbList` JSON-LD.
 *
 * Two jobs, both SEO:
 *  1. Free internal links on every interior page. The site's internal linking
 *     ran almost entirely through header/footer chrome, which left interior
 *     pages depending on nav alone to be discovered and weighted.
 *  2. Breadcrumb rich results in the SERP, which replace the raw URL line.
 *
 * The trail must mirror the URL path — Google cross-checks the two, and a
 * breadcrumb that contradicts the path is ignored.
 *
 * Renders the JSON-LD itself rather than routing through `PageSEO`, because
 * pages already pass their own page-level `jsonLd` and merging the two shapes
 * at every call site is more error-prone than emitting a second script tag
 * (multiple ld+json blocks per page are valid and independently parsed).
 */
import { useEffect, useMemo } from "react";

export type Crumb = {
  label: string;
  /** Omit on the final crumb — the current page is not a link. */
  href?: string;
};

const BASE_URL = "https://anter.ai";

export default function Breadcrumbs({ items }: { items: Crumb[] }) {
  // Callers pass an inline array literal, so `items` is a fresh reference on
  // every render. Memoise on its serialised value, or the effect below tears
  // the JSON-LD script down and rebuilds it on each render.
  const serialisedItems = JSON.stringify(items);
  const trail = useMemo<Crumb[]>(
    () => [{ label: "Home", href: "/" }, ...(JSON.parse(serialisedItems) as Crumb[])],
    [serialisedItems]
  );

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-breadcrumbs", "json-ld");
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: trail.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.label,
        ...(crumb.href ? { item: `${BASE_URL}${crumb.href === "/" ? "" : crumb.href}` } : {}),
      })),
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [trail]);

  return (
    // Layout-neutral on purpose: renders as the first child of each page's
    // existing <main> container so it inherits that page's width and padding.
    <nav aria-label="Breadcrumb" className="mb-6">
      <ol className="flex flex-wrap items-center gap-1.5 text-xs text-text-faint">
        {trail.map((crumb, index) => {
          const isLast = index === trail.length - 1;
          return (
            <li key={crumb.label} className="flex items-center gap-1.5">
              {crumb.href && !isLast ? (
                <a href={crumb.href} className="underline-offset-4 hover:text-text hover:underline">
                  {crumb.label}
                </a>
              ) : (
                <span aria-current={isLast ? "page" : undefined} className="text-text-muted">
                  {crumb.label}
                </span>
              )}
              {!isLast && <span aria-hidden="true">/</span>}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
